import { useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { ArrowDown2 } from "iconsax-react";
import type { PLReportResponse } from "@/features/accounting/financial-reports/types";
import { formatReportAmount, formatDateRange } from "@/features/accounting/financial-reports/utils";
import { getAccountName } from "@/features/accounting/shared";
import { toNumber } from "@/shared/utils";
import { useLocale } from "@/shared/hooks";
import { Skeleton } from "@/shared/ui";

interface PLReportCardProps {
  data?: PLReportResponse;
  isLoading?: boolean;
  error?: unknown;
  className?: string;
}

export default function PLReportCard({ data, isLoading, error, className }: PLReportCardProps) {
  const { t } = useTranslation("FinancialReportPage");
  const locale = useLocale();
  const [expandedExpenses, setExpandedExpenses] = useState<Set<string>>(new Set());

  if (isLoading) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <Skeleton width={300} height={24} className="mb-4" />
        <div className="space-y-4">
          <Skeleton width="100%" height={100} />
          <Skeleton width="100%" height={150} />
          <Skeleton width="100%" height={60} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <h3 className="text-body-bold-lg content-base-primary mb-4">{t("plReport.title")}</h3>
        <div className="text-body-regular-md content-action-neutral">
          {error ? t("plReport.error") : t("plReport.noData")}
        </div>
      </div>
    );
  }

  const netIncome = formatReportAmount(data.net_income);
  const expense_breakdown = data.expense_breakdown;
  const periodLabel = formatDateRange(data.start_date, data.end_date);

  interface BreakdownItem {
    label: string;
    value: number;
    key: string;
    subItems?: { label: string; value: number; key: string }[];
  }

  const breakdownItems: BreakdownItem[] = expense_breakdown
    ? (() => {
        const eb = expense_breakdown;
        const hasPayrollSplit = eb.staff_payroll_expense != null || eb.gph_payment_expense != null;
        const payrollItem: BreakdownItem = {
          label: t("plReport.breakdown.payroll"),
          value: toNumber(eb.payroll_expense),
          key: "payroll",
          subItems: hasPayrollSplit
            ? [
                { label: t("plReport.breakdown.staffPayroll"), value: toNumber(eb.staff_payroll_expense ?? "0"), key: "staffPayroll" },
                { label: t("plReport.breakdown.gphPayment"), value: toNumber(eb.gph_payment_expense ?? "0"), key: "gphPayment" },
              ].filter((s) => s.value !== 0)
            : undefined,
        };
        return [
          payrollItem,
          { label: t("plReport.breakdown.opvr"), value: toNumber(eb.opvr_expense), key: "opvr" },
          { label: t("plReport.breakdown.so"), value: toNumber(eb.so_expense), key: "so" },
          { label: t("plReport.breakdown.oosms"), value: toNumber(eb.oosms_expense), key: "oosms" },
          { label: t("plReport.breakdown.sn"), value: toNumber(eb.sn_expense), key: "sn" },
          { label: t("plReport.breakdown.purchase"), value: toNumber(eb.purchase_expense), key: "purchase" },
          { label: t("plReport.breakdown.depreciation"), value: toNumber(eb.depreciation_expense), key: "depreciation" },
          { label: t("plReport.breakdown.other"), value: toNumber(eb.other_expense), key: "other" },
        ].filter((item) => item.value !== 0);
      })()
    : [];

  const hasBreakdown = breakdownItems.length > 0;

  return (
    <div className={cn("py-5 px-6 radius-3xl surface-base-fill flex flex-col gap-5", className)}>
      <h3 className="text-body-bold-lg content-base-primary">{t("plReport.title")}</h3>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5">
          <p className="text-label-md content-base-primary">{t("plReport.revenueSection")}</p>
          <p className="text-label-sm content-base-low">{periodLabel}</p>
        </div>

        <div className="flex flex-col">
          {data.revenue.map((item) => {
            const amount = formatReportAmount(item.amount);
            return (
              <div key={item.code} className="py-3 border-t surface-container-stroke">
                <div className="flex justify-between items-center gap-5">
                  <p className="text-body-regular-md content-action-neutral">{getAccountName(item.code, locale)}</p>
                  <p className="text-body-bold-lg content-base-primary text-right">{amount.text}</p>
                </div>
              </div>
            );
          })}
          <div className="py-3 border-t surface-container-stroke">
            <div className="flex justify-between items-center gap-5">
              <p className="text-body-bold-md content-action-neutral">{t("plReport.totalRevenueLabel")}</p>
              <p className="text-body-bold-lg content-base-primary text-right">{formatReportAmount(data.total_revenue).text}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5">
          <p className="text-label-md content-base-primary">{t("plReport.expensesSection")}</p>
          <p className="text-label-sm content-base-low">{periodLabel}</p>
        </div>

        <div className="flex flex-col">
          {data.expenses.map((item) => {
            const amount = formatReportAmount(item.amount);
            const isExpanded = expandedExpenses.has(item.code);

            return (
              <div key={item.code}>
                <div className="py-3 border-t surface-container-stroke">
                  <div className="flex justify-between items-center gap-5">
                    {hasBreakdown ? (
                      <button
                        onClick={() =>
                          setExpandedExpenses((prev) => {
                            const next = new Set(prev);
                            if (next.has(item.code)) next.delete(item.code);
                            else next.add(item.code);
                            return next;
                          })
                        }
                        className="flex justify-start items-center gap-1 cursor-pointer">
                        <span
                          className={cn(
                            "content-action-neutral transition-transform duration-200",
                            !isExpanded && "-rotate-90",
                          )}>
                          <ArrowDown2 size={16} color="currentColor" />
                        </span>
                        <p className="text-body-regular-md content-action-neutral">{getAccountName(item.code, locale)}</p>
                      </button>
                    ) : (
                      <p className="text-body-regular-md content-action-neutral">{getAccountName(item.code, locale)}</p>
                    )}
                    <p className="text-body-bold-lg content-base-primary text-right">{amount.text}</p>
                  </div>
                </div>

                {hasBreakdown &&
                  isExpanded &&
                  breakdownItems.map((bdItem) => (
                    <div key={bdItem.key}>
                      <div className="py-3 border-t surface-container-stroke pl-6">
                        <div className="flex justify-between items-center gap-5">
                          <p className="text-body-regular-md content-action-neutral">{bdItem.label}</p>
                          <p className="text-body-bold-md content-base-primary text-right">
                            {formatReportAmount(bdItem.value).text}
                          </p>
                        </div>
                      </div>
                      {bdItem.subItems?.map((sub) => (
                        <div key={sub.key} className="py-2 border-t surface-container-stroke pl-12">
                          <div className="flex justify-between items-center gap-5">
                            <p className="text-body-regular-sm content-base-secondary">{sub.label}</p>
                            <p className="text-body-regular-sm content-base-secondary text-right">
                              {formatReportAmount(sub.value).text}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            );
          })}

          <div className="py-3 border-t surface-container-stroke">
            <div className="flex justify-between items-center gap-5">
              <p className="text-body-bold-md content-action-neutral">{t("plReport.totalExpensesLabel")}</p>
              <p className="text-body-bold-lg content-base-primary text-right">{formatReportAmount(data.total_expenses).text}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5 p-4 radius-md surface-component-fill">
          <p className="text-body-regular-md content-base-primary">{t("plReport.profitLoss")}</p>
          <p className="text-heading-lg text-right content-base-primary">
            {netIncome.text}
          </p>
        </div>

        <p className="text-body-regular-sm content-base-low text-center">{t("plReport.plFormula")}</p>
      </div>
    </div>
  );
}
